import { inject, Injectable } from '@angular/core';
import { collection, doc, Firestore, getDoc, getDocs, orderBy, query } from "@angular/fire/firestore";
import { BehaviorSubject, combineLatest, firstValueFrom, from,  of, Subject } from "rxjs";
import { getDocFromCache } from "firebase/firestore";
import { catchError, map, shareReplay, switchMap } from "rxjs/operators";
import { CommonService } from 'src/app/services/common/common.service';
import { environment } from 'src/environments/environment';
import { CatchErrorService } from '@core/catch-error.service';
import { AuthService } from '@core/auth.service';
import { Data } from '@angular/router';
import { CommonVariable } from 'src/app/app.constants';
import { PaymentDataService } from 'src/app/services/payment-data.service';

@Injectable({
  providedIn: 'root'
})
export class GameDatasService {
    constructor(
        private firestore: Firestore,
        private catchService: CatchErrorService,
        private authService: AuthService,
        private paymentDataService: PaymentDataService,
    ){}
    private commonService = inject(CommonService);
    selectedYoursToPlay$ = new BehaviorSubject(null);


    getGameInfo(gameId: string, type: string = 'default') {
        const result$ = new Subject<any>();
        this.commonService.getUid("GamesRepositoryService").subscribe({
        next: (userId) => {
            if (!userId || !gameId || !type) {
            result$.next([null, null]);
            result$.complete();
            return;
            }
            /* -----------------------------------------------
            * Build Firestore paths
            * ------------------------------------------------ */
            const userGamePath = `users/${userId}/game/${type}/${gameId}/details`;
            const generalGamePath = `general_modules/game/${environment.mirrorPath}${type}/${gameId}`;
            /* -----------------------------------------------
            * Create references
            * ------------------------------------------------ */
            const userGameDetailsRef = doc(this.firestore, userGamePath);
            const generalGameInfoRef = doc(this.firestore, generalGamePath);
            /* -----------------------------------------------
            * Fetch with cache fallback
            * ------------------------------------------------ */
            const userGame$ = from(
            getDocFromCache(userGameDetailsRef).catch(() => {
                return getDoc(userGameDetailsRef);
            })
            );

            const generalGame$ = from(
            getDocFromCache(generalGameInfoRef).catch(() => {
                return getDoc(generalGameInfoRef);
            })
            );
            combineLatest([userGame$, generalGame$]).pipe(shareReplay(1)).subscribe({
            next: (docs) => {
                result$.next(docs);
            },

            error: (err) => {
                // 🔥 Detect Permission Denied
                if (err?.code === "permission-denied" ||
                err?.message?.includes("Missing or insufficient permissions")) {
                }
                result$.error(err);
            },
            complete: () => {
                result$.complete();
            }
            });
        },

        error: (err) => {
            result$.error(err);
        }
        });

        return result$.asObservable();
    }

    getGameInfoY2P(gameId: string, type?: string) {
    return this.catchService.isOnline$
      .pipe(
        switchMap((isOnline) => {
          if (!isOnline) return of(null);
          return this.authService.getUid$("GamesRepositoryService").pipe(
            switchMap(async (userId) => {
              if (!userId) return null;
              // Modular Firestore API: Reference the collection
              const detailsCollectionRef = collection(
                this.firestore,
                `/users/${userId}/game/${type}/details`
              );

              // Query for ordering by "updated_opt_msg"
              const orderedQuery = query(detailsCollectionRef, orderBy("updated_opt_msg", "desc"));

              // Fetch query results (all documents)
              const querySnapshot = await getDocs(orderedQuery);

              // Get specific document data by ID (gameId)
              const documentSnapshot = gameId ? querySnapshot.docs.find((doc) => doc.id === gameId) : null;
              return documentSnapshot ? documentSnapshot.data() : null;
            })
          );
        }),
        this.catchService.retryWhen,
        this.catchService.catchOfflineError,
        shareReplay(1)
      )
      .pipe(
        map((res: Data) => res),
        catchError((err) => {
          return of(null);
        })
      );
    }


    getFlashSaleData(flashSaleDetails: Data) {
        let flashSalesExpiryDate: Data;
        let salesCost: number;
        if (Array.isArray(flashSaleDetails)) {
        const sortedData = flashSaleDetails.sort((a, b) => a.flash_sale_date.seconds - b.flash_sale_date.seconds);
        flashSalesExpiryDate = sortedData[0]?.flash_sale_date;
        salesCost = sortedData[0]?.sales_cost;
        } else {
        flashSalesExpiryDate = flashSaleDetails?.flash_sale_date;
        salesCost = flashSaleDetails?.sales_cost;
        }
        return {
        flash_sale_date: flashSalesExpiryDate,
        sales_cost: salesCost
        }
    }

    isFlashSaleToday(flashSaleTimestamp: Data) {
    try {
      if (!flashSaleTimestamp) return false;
      // Convert Firestore timestamp to a Date object
      const flashSaleDate = new Date(flashSaleTimestamp?.seconds * 1000);
      const currentDate = new Date();

      // Extract only the date part (YYYY-MM-DD) for comparison
      const flashSaleDateOnly = flashSaleDate.toISOString().split('T')[0];
      const currentDateOnly = currentDate.toISOString().split('T')[0];

      if (flashSaleDateOnly > currentDateOnly) {
        return false;
      } else if (flashSaleDateOnly < currentDateOnly) {
        return false;
      } else {
        return true;
      }
    } catch (err) {
      return false;
    }
    }

    setY2PGameData(game: any) {
        this.selectedYoursToPlay$.next(game);
    }

    getY2PGameData() {
        return this.selectedYoursToPlay$;
    }

    async getDVCurrency(currencyValue: string): Promise<string> {
        const CurrencySymbol = "£";
        const isEmpty = (val: string) =>
          val === undefined || val === null || !(Object.keys(val) || val).length;
    
        if (isEmpty(currencyValue)) return CurrencySymbol + "0";
    
        currencyValue = currencyValue.replace(",", "");
        let formatedDVData = currencyValue.replace("[", "").replace("]", "");
        let result = formatedDVData?.split(":");
        let dvKey1 = result[0];
        const dvRoundingCondition = dvKey1 === "currency_beauty" || dvKey1 === "currency_beautify";
        let dvKey2 = dvRoundingCondition ? Number(result[1])?.toFixed(2) : result[1];
    
        const [user, data] = await firstValueFrom(
          combineLatest([
            this.authService.userDetails$,
            this.authService.getCurrencyList$
          ])
        );
    
        const preferredCurrency = (user as Data)?.preferred_currency ?? CommonVariable.preferredCurrency;
        const CurrencyDetails = (data as Data)?.details?.find(
          (r: Data) => r?.currency_code === preferredCurrency
        );
        const currencySymbol = CurrencyDetails?.currency_symbol ?? CurrencySymbol;
    
        if (dvRoundingCondition) {
          const beautifiedAmt = await this.paymentDataService
            .getBeautifiedCurrency(dvKey2, preferredCurrency);
          return beautifiedAmt ? currencySymbol + beautifiedAmt : currencySymbol + "0";
        }
    
        return currencySymbol + "0";
    }

    updateGamePlayLastSession(sessionId: string) {
        if (!sessionId) return;
        try {
          const userId = this.authService.getUid$("GamesRepositoryService").pipe(
            switchMap((userId) => {
              return of(userId) || of(null);
            }));
          const path_id = "Hf9Vr0H8IP45F23czeRh";
          const doc_data = { parent_game_id: sessionId };
          const method = "update";
    
          this.authService.getV2CloudFunction("updatefirestoredoc", {
            path_id,
            doc_data,
            method,
          }).subscribe({
            error: (err) => {
              return err;
            }
          });
        } catch (error) {
          return error;
        }
    }

}